import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Lock, Loader2, AlertTriangle, ArrowRight, Zap, TrendingUp,
  CheckCircle2, ChevronRight, Compass, Wrench, ShieldAlert, X, RefreshCw,
} from "lucide-react";

// Automation Readiness. Read-only. Bridge between Historical
// Performance (the symptom) and Setup (the cure). The page never writes;
// every CTA is a deep link to an existing setup screen.

const IMPACT_CHIP = {
  high:   { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-800",    label: "High Impact"   },
  medium: { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   label: "Medium Impact" },
  low:    { bg: "bg-slate-50",   border: "border-slate-200",   text: "text-slate-700",   label: "Low Impact"    },
};
const BUCKET = {
  ready:             { label: "Ready",             chip: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  approaching_ready: { label: "Approaching Ready", chip: "bg-blue-100 text-blue-800 border-blue-200" },
  needs_setup_work:  { label: "Needs Setup Work",  chip: "bg-amber-100 text-amber-800 border-amber-200" },
};
const LADDER = [
  { key: "L1", label: "Insights" },
  { key: "L2", label: "Recommendations" },
  { key: "L3", label: "Draft Automation" },
  { key: "L4", label: "Auto Apply" },
  { key: "L5", label: "Autonomous Workforce" },
];

export default function AutomationReadinessPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cem, setCem] = useState(null);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get("/automation/blockers"),
        api.get("/automation/cem/status").catch(() => ({ data: null })),
      ]);
      setData(b.data);
      setCem(c.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load Automation Readiness");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function recheckCem() {
    setRechecking(true);
    try {
      const { data } = await api.post("/automation/cem/evaluate", { apply_if_required: false });
      setCem((prev) => ({ ...(prev || {}), verdict: data.verdict,
        can_resume: (prev?.cem_paused && data.verdict?.recommended_action === "ok") }));
      const hard = (data.verdict?.triggers || []).filter((t) => t.severity === "hard").length;
      if (hard === 0 && cem?.cem_paused) {
        toast.success("No remaining blockers, you can restore your automation level.");
      } else if (hard > 0) {
        toast(`${hard} blocker${hard === 1 ? "" : "s"} still active.`);
      } else {
        toast.success("Re-check complete.");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Re-check failed");
    } finally { setRechecking(false); }
  }

  if (loading) {
    return <div className="py-10 text-center text-sm text-[#4B5773]"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading Automation Readiness…</div>;
  }
  if (!data) return null;

  const bucket = BUCKET[data.bucket] || BUCKET.needs_setup_work;
  const journey = data.journey || {};
  const visibleBlockers = (data.blockers || []).filter((b) => b.count > 0);
  const cemPaused = !!cem?.cem_paused;
  const cemTriggers = (cem?.verdict?.triggers || []).filter((t) => t.severity === "hard");

  return (
    <div data-testid="automation-readiness-page">
      {cemPaused && (
        <CemPauseBanner
          cem={cem}
          triggers={cemTriggers}
          rechecking={rechecking}
          onRecheck={recheckCem}
          onResume={() => setResumeOpen(true)}
        />
      )}
      <div className="mb-6">
        <div className="eyebrow flex items-center gap-2">
          Automation
          <span className="text-[10px] uppercase tracking-widest text-blue-800 px-1.5 py-0.5 rounded bg-blue-100 border border-blue-200">
            Read-only · No live changes
          </span>
        </div>
        <h1 className="font-display text-4xl mt-1 flex items-center gap-2">
          <Compass className="h-7 w-7 text-[#2563EB]" />
          Automation Readiness
        </h1>
        <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
          These are the specific reasons automation cannot help yet, and the actions required to improve automation performance. Fixing items below typically lifts your Historical Performance trust score.
        </p>
      </div>

      {/* Quick Wins, fastest improvements, top of page to create momentum */}
      {(data.quick_wins || []).length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 mb-5" data-testid="readiness-quick-wins">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-4 w-4 text-emerald-700" />
            <h3 className="font-medium text-sm text-[#0B1A36]">Fastest Improvements</h3>
            <span className="text-[11px] text-[#4B5773] ml-auto">The smallest changes with the biggest automation benefit.</span>
          </div>
          <ul className="space-y-1.5">
            {data.quick_wins.map((q, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
                <div className="flex-1">
                  <strong className="text-[#0B1A36]">{q.label}</strong>
                  <span className="text-[#4B5773]"> · {q.count} item{q.count === 1 ? "" : "s"}</span>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${IMPACT_CHIP[q.impact_band].bg} ${IMPACT_CHIP[q.impact_band].border} ${IMPACT_CHIP[q.impact_band].text}`}>
                  {IMPACT_CHIP[q.impact_band].label}
                </span>
                <a href={q.setup_link} className="text-xs text-[#2563EB] hover:underline" data-testid={`quick-win-link-${q.blocker_key}`}>
                  Fix in Setup <ArrowRight className="h-3 w-3 inline" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Blockers headline, primary; readiness score sits BELOW as supporting */}
      <div className="rounded-lg border-2 border-rose-200 bg-gradient-to-br from-rose-50/40 to-white p-5 mb-5" data-testid="readiness-headline">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="h-5 w-5 text-rose-700" />
          <h2 className="text-2xl text-[#0B1A36] font-medium" data-testid="readiness-blocker-headline">
            {data.critical_blocker_count} {data.critical_blocker_count === 1 ? "blocker" : "blockers"} preventing higher automation
          </h2>
        </div>
        <p className="text-sm text-[#4B5773]">
          Each blocker below explains <strong>what is missing</strong> and <strong>why it matters</strong>. Resolving the High Impact ones first usually delivers the largest improvement.
        </p>
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <span className="text-xs text-[#4B5773]">Readiness Score:</span>
          <span className="text-xl font-medium text-[#0B1A36]" data-testid="readiness-score">{data.score}/100</span>
          <span className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded border ${bucket.chip}`}>
            {bucket.label}
          </span>
          <span className="text-[11px] text-[#64748B]">
            {data.total_blocker_families - visibleBlockers.length} of {data.total_blocker_families} blocker families are clear
          </span>
        </div>
      </div>

      {/* Automation Journey */}
      {journey.next_level && (
        <div className="rounded-lg border border-[#DBE7FE] bg-[#F6F9FE] p-4 mb-5" data-testid="automation-journey">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-[#2563EB]" />
            <h3 className="font-medium text-sm text-[#0B1A36]">Automation Journey</h3>
          </div>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="min-w-[180px]">
              <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Current Level</div>
              <div className="text-base font-medium text-[#0B1A36]">{journey.current_level_label}</div>
            </div>
            <div className="min-w-[180px]">
              <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Next Level</div>
              <div className="text-base font-medium text-[#0B1A36]">{journey.next_level_label}</div>
            </div>
            <div className="min-w-[180px]">
              <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Progress</div>
              <div className="text-base font-medium text-[#0B1A36]">
                {journey.requirements_met} / {journey.requirements_total} requirements met
              </div>
              <div className="mt-1 h-1.5 bg-slate-200 rounded overflow-hidden w-full max-w-[200px]">
                <div className="h-full bg-[#2563EB]" style={{ width: `${journey.requirements_total ? Math.round(100 * (journey.requirements_met || 0) / journey.requirements_total) : 0}%` }} />
              </div>
            </div>
          </div>
          {(journey.top_blockers || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#DBE7FE]">
              <div className="text-[10px] uppercase tracking-widest text-[#64748B] mb-1">Top Blockers</div>
              <ul className="text-sm text-[#0B1A36] space-y-0.5">
                {journey.top_blockers.map((b, i) => (
                  <li key={i}>· {b.label} <span className="text-[#4B5773]">({b.count})</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Maturity Ladder */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-5" data-testid="readiness-ladder">
        <h3 className="font-medium text-sm text-[#0B1A36] mb-3">Automation Maturity Ladder</h3>
        <ol className="flex items-center gap-1 overflow-x-auto">
          {LADDER.map((step, i) => {
            const idx = LADDER.findIndex((s) => s.key === journey.current_level);
            const reached = i <= idx;
            const next = i === idx + 1;
            return (
              <li key={step.key} className={`flex items-center gap-1 shrink-0 px-3 py-1.5 rounded text-xs ${
                reached ? "bg-emerald-100 text-emerald-900 border border-emerald-200"
                        : next ? "bg-blue-100 text-blue-900 border border-blue-200"
                               : "bg-slate-50 text-slate-500 border border-slate-200"}`}>
                <strong>{step.key}</strong>
                <span>{step.label}</span>
                {i < LADDER.length - 1 && <ChevronRight className="h-3 w-3 text-slate-400 mx-1" />}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Blocker families grid */}
      <div className="grid lg:grid-cols-2 gap-3 mb-5" data-testid="readiness-blockers-grid">
        {(visibleBlockers.length > 0 ? visibleBlockers : (data.blockers || [])).map((b) => (
          <BlockerCard key={b.key} blocker={b} />
        ))}
      </div>

      {data.latest_replay && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="font-medium text-sm text-[#0B1A36] mb-1">Linked to your latest Historical Performance</h3>
          <p className="text-sm text-[#0B1A36]">
            Trust score <strong>{data.latest_replay.result_summary?.trust_score_pct ?? 0}%</strong>{" "}
            over {data.latest_replay.scope?.from} → {data.latest_replay.scope?.to}.{" "}
            Resolving the blockers above is the most direct way to lift it.
          </p>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate("/app/replays")}>
            Open Historical Performance <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      )}

      {resumeOpen && (
        <CemResumeModal
          cem={cem}
          onClose={() => setResumeOpen(false)}
          onResumed={async () => { setResumeOpen(false); await fetchData(); }}
        />
      )}
    </div>
  );
}

function CemPauseBanner({ cem, triggers, rechecking, onRecheck, onResume }) {
  const fromLevel = cem?.cem_paused_from_level || "L3";
  const toLevel = cem?.current_level || "L2";
  const pausedAt = cem?.cem_paused_at
    ? new Date(cem.cem_paused_at).toLocaleString("en-GB",
        { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })
    : null;
  return (
    <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-4 mb-5"
         data-testid="cem-pause-banner">
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-rose-700 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-base text-rose-900">
              Automation paused: {fromLevel} → {toLevel}
            </h3>
            <span className="text-[10px] uppercase tracking-widest text-rose-800 px-1.5 py-0.5 rounded bg-rose-100 border border-rose-200">
              Safety pause
            </span>
            {pausedAt && (
              <span className="text-xs text-rose-700 ml-auto">Paused {pausedAt}</span>
            )}
          </div>
          <p className="text-sm text-rose-900 mt-1">
            NextRota stepped your automation level down because the setup changed in a way that
            isn&apos;t safe for {fromLevel}. Your live rota, leave and shifts are untouched.
            Once you resolve the items below, click <em>Restore automation level</em>.
          </p>
          {triggers.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="cem-pause-triggers">
              {triggers.slice(0, 3).map((t, i) => (
                <li key={i} className="text-sm text-rose-900">
                  <strong>· {t.label}</strong>
                  {t.fix_hint && <span className="text-rose-800">, {t.fix_hint}</span>}
                  {t.deep_link && (
                    <Link to={t.deep_link}
                          className="ml-2 text-xs underline text-rose-900 hover:text-rose-700"
                          data-testid={`cem-pause-trigger-link-${t.code}`}>
                      Open setup
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline"
                    className="border-rose-300 text-rose-900 hover:bg-rose-100"
                    onClick={onRecheck} disabled={rechecking}
                    data-testid="cem-recheck-btn">
              {rechecking ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Re-check now
            </Button>
            <Button size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={!cem?.can_resume}
                    onClick={onResume}
                    data-testid="cem-resume-btn">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Restore automation level…
            </Button>
            {!cem?.can_resume && (
              <span className="text-xs text-rose-800">Resolve the items above to enable restore.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CemResumeModal({ cem, onClose, onResumed }) {
  const fromLevel = cem?.cem_paused_from_level || "L3";
  const [target, setTarget] = useState(fromLevel);
  const [restoreActions, setRestoreActions] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function confirm() {
    setSubmitting(true);
    try {
      const { data } = await api.post("/automation/cem/resume", {
        target_level: target, confirm: true, restore_actions: restoreActions,
      });
      toast.success(`Automation restored to ${data.current_level}.`);
      onResumed();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || formatError(detail) || "Restore failed");
      toast.error(msg);
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
         data-testid="cem-resume-modal">
      <div className="bg-white rounded-lg shadow-2xl w-[560px] max-w-[92vw] overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h3 className="font-medium text-base text-[#0B1A36]">Restore automation level</h3>
          <button type="button" onClick={onClose}
                  className="ml-auto text-slate-500 hover:text-slate-700"
                  data-testid="cem-resume-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm text-[#0B1A36]">
          <p>
            NextRota auto-paused you at <strong>{cem?.current_level}</strong> on a previous setup change.
            We&apos;ve just re-checked and all blocking items are clear. Choose the level to restore.
          </p>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#64748B] block mb-1">
              Restore to
            </label>
            <select value={target} onChange={(e) => setTarget(e.target.value)}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="cem-resume-level-select">
              <option value={fromLevel}>{fromLevel} (where you were)</option>
            </select>
            <p className="text-[11px] text-[#64748B] mt-1">
              You can only restore up to the level you were on before the pause.
              Higher levels require the normal upgrade flow.
            </p>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={restoreActions}
                   onChange={(e) => setRestoreActions(e.target.checked)}
                   className="mt-1"
                   data-testid="cem-resume-restore-actions" />
            <span>
              Also re-enable the auto-actions that were suspended at pause time. Off by default,               tick this only if you&apos;re sure the underlying issue is fully resolved.
            </span>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="cem-resume-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={confirm} disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="cem-resume-confirm-btn">
            {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
            Yes, restore to {target}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BlockerCard({ blocker }) {
  const impact = IMPACT_CHIP[blocker.impact_band] || IMPACT_CHIP.low;
  const cleared = (blocker.count || 0) === 0;
  return (
    <div className={`rounded-lg border-2 ${cleared ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"} p-4`}
         data-testid={`blocker-card-${blocker.key}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <h4 className="text-base font-medium text-[#0B1A36] flex items-center gap-1">
            {cleared && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            {blocker.label}
          </h4>
          <p className="text-sm text-[#4B5773] mt-0.5">
            {cleared
              ? "All clear, no items found in this family."
              : <><strong>{blocker.count}</strong> item{blocker.count === 1 ? "" : "s"} affected
                  {blocker.denominator && blocker.denominator > 1
                    ? ` of ${blocker.denominator}`
                    : ""}.</>}
          </p>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${impact.bg} ${impact.border} ${impact.text} shrink-0`}>
          {impact.label}
        </span>
      </div>

      {!cleared && (
        <>
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-widest text-[#64748B] mb-1">Impact on automation</div>
            <ul className="text-xs text-[#0B1A36] space-y-0.5 list-disc list-inside">
              {(blocker.business_impact || []).map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <a href={blocker.setup_link} className="text-xs text-[#2563EB] hover:underline" data-testid={`blocker-fix-link-${blocker.key}`}>
              <Wrench className="h-3 w-3 inline mr-1" />Fix in Setup
            </a>
            {(blocker.sample || []).length > 0 && (
              <span className="text-[11px] text-[#64748B] ml-auto">
                Affects {Math.min(blocker.sample.length, blocker.count)} of {blocker.count}: {blocker.sample.slice(0, 2).map(r => r.employee_name || r.name || r.reason || r.hint).filter(Boolean).join(", ") || "-"}{blocker.count > 2 ? "…" : ""}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
